import { Box, CircularProgress } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

import { PopupActionButton, PopupGhostButton, PopupProcessingButton } from "../PopupButtons";
import {
  CalloutText,
  DetailBody,
  DetailCard,
  DetailTitle,
  ErrorNote,
  InfoCallout,
  figmaButtonSx,
} from "../Settlement/settlementStyles";
import type { useVerdictSignatures } from "../../hooks/useVerdictSignatures";

// The seat's side of a server-collected determination (V10.1.2). Replaces PanelSignatureCollector
// for EcoChallenge.recordOutcome whenever the backend's verdict-signature endpoint is up: the seat
// picks the two answers (in the dialog above), clicks Sign, and watches the tally. Nothing is
// pasted and nothing costs gas — the server submits recordOutcome once one verdict has enough.
//
// The tally is per VERDICT, not per seat: the digest covers upheld AND inBreach, so a seat that
// signs "uphold, no breach" does not count toward "uphold, breach". The other verdicts that have
// signatures are listed underneath so a seat can see where the panel is leaning and, if it
// agrees, sign the same pair — two seats on different pairs never reach a decision.

export type ServerVerdict = ReturnType<typeof useVerdictSignatures>;

export interface VerdictSignatureTallyProps {
  lane: ServerVerdict;
  // The verdict currently picked in the dialog.
  upheld: boolean;
  inBreach: boolean;
  // Connected wallet, to say whether it holds a seat and has signed.
  viewerAddress?: string | null;
  onCancel: () => void;
}

const shortAddress = (addr: string) => `${addr.slice(0, 6)}…${addr.slice(-4)}`;

export const verdictLabel = (upheld: boolean, inBreach: boolean) =>
  `${upheld ? "Uphold" : "Dismiss"} · ${inBreach ? "in breach" : "not in breach"}`;

const VerdictSignatureTally = ({ lane, upheld, inBreach, viewerAddress, onCancel }: VerdictSignatureTallyProps) => {
  const { status, loading, signing, error, sign } = lane;
  const viewer = viewerAddress?.toLowerCase() ?? null;

  if (!status) {
    return (
      <>
        {error && <ErrorNote>{error}</ErrorNote>}
        {loading && (
          <PopupProcessingButton disabled variant="contained" fullWidth sx={figmaButtonSx}>
            <CircularProgress size={20} sx={{ color: "white", mr: "10px" }} />
            Reading the panel…
          </PopupProcessingButton>
        )}
        <PopupGhostButton onClick={onCancel} fullWidth>
          Cancel
        </PopupGhostButton>
      </>
    );
  }

  const { panel, threshold, verdicts } = status;
  const selected = verdicts.find((v) => v.upheld === upheld && v.inBreach === inBreach);
  const signedSelected = new Set(selected?.signers ?? []);
  const count = selected?.count ?? 0;
  const others = verdicts.filter((v) => !(v.upheld === upheld && v.inBreach === inBreach) && v.count > 0);
  // Any verdict at the threshold is enough: the server queues recordOutcome for the first one there.
  const decided = verdicts.find((v) => v.count >= threshold && threshold > 0) ?? null;
  const viewerIsSeat = !!viewer && panel.includes(viewer);
  const viewerSignedThis = !!viewer && signedSelected.has(viewer);
  const needed = Math.max(0, threshold - count);

  return (
    <>
      <DetailCard>
        <DetailTitle>
          {threshold === 1 ? "Checker" : "Panel"} — {Math.min(count, threshold)} of {threshold} signature
          {threshold === 1 ? "" : "s"} on this verdict
        </DetailTitle>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", mt: "6px" }}>
          {panel.map((seat) => {
            const signed = signedSelected.has(seat);
            return (
              <Box key={seat} sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {signed ? (
                  <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "#2E7D32" }} />
                ) : (
                  <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: "#999" }} />
                )}
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "13px", color: "#000" }}>
                  {shortAddress(seat)}
                </span>
                {seat === viewer && <span style={{ fontSize: "12px", color: "#888" }}>(you)</span>}
              </Box>
            );
          })}
        </Box>
        {others.length > 0 && (
          <DetailBody style={{ marginTop: "10px", color: "#666" }}>
            Also signed:{" "}
            {others.map((v) => `${verdictLabel(v.upheld, v.inBreach)} (${v.count} of ${threshold})`).join(", ")}. Seats
            must sign the same pair of answers for it to count.
          </DetailBody>
        )}
        {!viewerIsSeat && (
          <DetailBody style={{ marginTop: "8px" }}>
            Your wallet does not hold a seat on this panel. The seats above sign from their own wallets; this tally
            updates as they do.
          </DetailBody>
        )}
      </DetailCard>

      {decided ? (
        <InfoCallout surface="white" tone="progress">
          <CalloutText>
            <strong>{verdictLabel(decided.upheld, decided.inBreach)}</strong> has enough signatures. The server is
            recording the determination on chain — this usually takes a minute or two, and this dialog updates when it
            lands.
          </CalloutText>
        </InfoCallout>
      ) : (
        needed > 0 && (
          <InfoCallout surface="white">
            <CalloutText>
              {needed} more signature{needed === 1 ? "" : "s"} needed on this verdict. Once{" "}
              {threshold === 1 ? "it is" : "they are"} in, the server records it — no gas, and nobody has to submit
              anything.
            </CalloutText>
          </InfoCallout>
        )
      )}

      {error && <ErrorNote>{error}</ErrorNote>}

      {viewerIsSeat &&
        !decided &&
        (signing ? (
          <PopupProcessingButton disabled variant="contained" fullWidth sx={figmaButtonSx}>
            <CircularProgress size={20} sx={{ color: "white", mr: "10px" }} />
            Waiting for your wallet signature…
          </PopupProcessingButton>
        ) : (
          <PopupActionButton
            onClick={() => sign(upheld, inBreach)}
            disabled={viewerSignedThis}
            variant="contained"
            fullWidth
            sx={figmaButtonSx}
          >
            {viewerSignedThis ? "You have signed this verdict" : "Sign this verdict — no gas"}
          </PopupActionButton>
        ))}
      <PopupGhostButton onClick={onCancel} disabled={signing} fullWidth>
        Close
      </PopupGhostButton>
    </>
  );
};

export default VerdictSignatureTally;
